import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Copy, Loader2, Package, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type PackageTier = "good" | "better" | "best";
export type PackageLineSection = "material" | "labor";

export interface CatalogItemOption {
  id: string;
  name: string;
  unit_type: string | null;
  unit_price: number | null;
  type: string | null;
  manufacturer: string | null;
  category: string | null;
  subcategory: string | null;
  tier?: string | null;
  xactimate_code?: string | null;
}

export interface CatalogPackageItem {
  id: string;
  package_group_id: string;
  catalog_item_id: string | null;
  quantity?: number | null;
  section: PackageLineSection | null;
  default_qty_formula: string | null;
  sort_order: number | null;
  custom_label: string | null;
  catalog_item?: CatalogItemOption | null;
}

export interface CatalogPackageGroup {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  manufacturer: string | null;
  tier: string | null;
  sku: string | null;
  is_system: boolean | null;
  sort_order: number | null;
  cloned_from_id: string | null;
  items?: CatalogPackageItem[];
}

export interface DraftLine {
  id?: string;
  catalog_item_id: string | null;
  section: PackageLineSection;
  default_qty_formula: string;
  sort_order: number;
}

export interface PackageEditorState {
  open: boolean;
  source: CatalogPackageGroup | null;
  cloneMode: boolean;
}

interface CatalogPackageBuilderDialogProps {
  open: boolean;
  source: CatalogPackageGroup | null;
  cloneMode?: boolean;
  catalogItems: CatalogItemOption[];
  saving?: boolean;
  onClose: () => void;
  onSave: (payload: {
    sourceId?: string;
    cloneMode: boolean;
    values: {
      name: string;
      category: string;
      manufacturer: string;
      tier: PackageTier | "";
      description: string;
      lines: DraftLine[];
    };
  }) => Promise<void>;
}

interface CatalogPackageSystemsTabProps {
  packages: CatalogPackageGroup[];
  catalogItems: CatalogItemOption[];
  searchQuery: string;
  categoryFilter: string;
  loading?: boolean;
  saving?: boolean;
  onCreate: () => void;
  onEdit: (pkg: CatalogPackageGroup) => void;
  onClone: (pkg: CatalogPackageGroup) => void;
  onDelete: (pkg: CatalogPackageGroup) => void;
}

const FORMULA_PRESETS = [
  { label: "Per Square (×1.10 waste)", value: "SQ * 1.10" },
  { label: "Starter (Eaves + Rakes) / 100", value: "(LF_EAVE + LF_RAKE) / 100" },
  { label: "Hip & Ridge / 25", value: "(LF_RIDGE + LF_HIP) / 25" },
  { label: "Ice & Water", value: "(LF_EAVE * 3 + LF_HIP + LF_VALLEY) / 66" },
  { label: "Synthetic Underlayment", value: "SQ" },
  { label: "Drip Edge", value: "LF_EAVE + LF_RAKE" },
  { label: "Flat 1", value: "1" },
];

const SAMPLE_MEASUREMENTS = {
  SQ: 30,
  LF_EAVE: 120,
  LF_RAKE: 80,
  LF_RIDGE: 40,
  LF_HIP: 0,
  LF_VALLEY: 30,
  LF_GUTTER: 200,
  EA_VENT: 4,
  EA_PIPE: 3,
};

const TIER_BADGE: Record<string, string> = {
  good: "bg-muted text-muted-foreground",
  better: "bg-primary/15 text-primary",
  best: "bg-accent text-accent-foreground",
};

const qtyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function evaluateFormula(formula: string | null | undefined) {
  const normalized = (formula || "1").trim();
  if (!normalized) return 1;

  const replaced = normalized.replace(/\b(SQ|LF_EAVE|LF_RAKE|LF_RIDGE|LF_HIP|LF_VALLEY|LF_GUTTER|EA_VENT|EA_PIPE)\b/g, (token) => {
    return String(SAMPLE_MEASUREMENTS[token as keyof typeof SAMPLE_MEASUREMENTS] ?? 0);
  });

  if (!/^[0-9+\-*/().\s]+$/.test(replaced)) return 1;

  try {
    const result = Function(`"use strict"; return (${replaced});`)();
    if (typeof result !== "number" || !Number.isFinite(result)) return 1;
    return Math.max(result, 0);
  } catch {
    return 1;
  }
}

function PackageItemCombobox({ items, value, onChange }: { items: CatalogItemOption[]; value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((item) => item.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? `${selected.name} (${selected.unit_type || "unit"})` : "Search items…"}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[380px] p-0" align="start">
        <Command
          filter={(candidate, search) => {
            const item = items.find((entry) => entry.id === candidate);
            if (!item) return 0;
            const haystack = `${item.name} ${item.manufacturer || ""} ${item.subcategory || ""}`.toLowerCase();
            return haystack.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search catalog items…" autoFocus />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onChange(item.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-3 w-3", value === item.id ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0">
                    <div className="truncate text-sm">{item.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[item.manufacturer, item.subcategory, item.tier].filter(Boolean).join(" • ") || item.category}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function CatalogPackageBuilderDialog({ open, source, cloneMode = false, catalogItems, saving, onClose, onSave }: CatalogPackageBuilderDialogProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Roofing");
  const [manufacturer, setManufacturer] = useState("");
  const [tier, setTier] = useState<PackageTier | "">("");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);

  useEffect(() => {
    if (!open) return;

    if (source) {
      setName(cloneMode ? `${source.name} (Copy)` : source.name);
      setCategory(source.category || "Roofing");
      setManufacturer(source.manufacturer || "");
      setTier((source.tier as PackageTier | "") || "");
      setDescription(source.description || "");
      setLines(
        (source.items || []).map((item, index) => ({
          id: cloneMode ? undefined : item.id,
          catalog_item_id: item.catalog_item_id,
          section: item.section || (item.catalog_item?.type === "Labor" ? "labor" : "material"),
          default_qty_formula: item.default_qty_formula || item.quantity?.toString() || "1",
          sort_order: item.sort_order ?? index,
        }))
      );
      return;
    }

    setName("");
    setCategory("Roofing");
    setManufacturer("");
    setTier("");
    setDescription("");
    setLines([]);
  }, [open, source, cloneMode]);

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        catalog_item_id: null,
        section: "material",
        default_qty_formula: "1",
        sort_order: prev.length,
      },
    ]);
  };

  const updateLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, ...patch } : line)));
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, idx) => idx !== index).map((line, idx) => ({ ...line, sort_order: idx })));
  };

  const save = async () => {
    await onSave({
      sourceId: source?.id,
      cloneMode,
      values: { name, category, manufacturer, tier, description, lines },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {source && !cloneMode && !(source.is_system ?? false)
              ? "Edit Package System"
              : cloneMode
                ? "Clone Package System"
                : "New Package System"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Name *</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g., GAF Timberline HDZ — Better" />
          </div>
          <div>
            <Label>Trade</Label>
            <Input value={category} onChange={(event) => setCategory(event.target.value)} placeholder="Roofing" />
          </div>
          <div>
            <Label>Manufacturer</Label>
            <Input value={manufacturer} onChange={(event) => setManufacturer(event.target.value)} placeholder="GAF, Owens Corning, CertainTeed…" />
          </div>
          <div>
            <Label>Tier</Label>
            <Select value={tier || undefined} onValueChange={(value: PackageTier) => setTier(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="better">Better</SelectItem>
                <SelectItem value="best">Best</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} placeholder="Explain what this package includes and where it fits." />
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">Line Items + Qty Logic</Label>
            <Button size="sm" variant="outline" onClick={addLine}>
              <Plus className="mr-1 h-4 w-4" /> Add Line
            </Button>
          </div>

          {lines.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
              No lines yet. Add shingles, starter, hip & ridge, underlayment, labor, or any other catalog item.
            </div>
          ) : (
            <div className="space-y-2">
              {lines.map((line, index) => {
                const selectedItem = catalogItems.find((item) => item.id === line.catalog_item_id);
                return (
                  <div key={`${line.id || "new"}-${index}`} className="grid grid-cols-12 gap-2 rounded-md border border-border p-3">
                    <div className="col-span-5">
                      <PackageItemCombobox
                        items={catalogItems}
                        value={line.catalog_item_id || ""}
                        onChange={(value) => {
                          const item = catalogItems.find((entry) => entry.id === value);
                          updateLine(index, {
                            catalog_item_id: value,
                            section: item?.type === "Labor" ? "labor" : "material",
                          });
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <Select value={line.section} onValueChange={(value: PackageLineSection) => updateLine(index, { section: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="labor">Labor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Input value={line.default_qty_formula} onChange={(event) => updateLine(index, { default_qty_formula: event.target.value })} className="font-mono text-xs" placeholder="SQ * 1.10" />
                    </div>
                    <div className="col-span-1">
                      <Select value={line.default_qty_formula} onValueChange={(value) => updateLine(index, { default_qty_formula: value })}>
                        <SelectTrigger className="text-xs">
                          <SelectValue placeholder="Preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {FORMULA_PRESETS.map((preset) => (
                            <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="icon" variant="ghost" onClick={() => removeLine(index)} className="h-9 w-9 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {selectedItem && (
                      <div className="col-span-12 text-[11px] text-muted-foreground">
                        {selectedItem.manufacturer || selectedItem.category} • {selectedItem.subcategory || "General"} • {selectedItem.unit_type || "unit"} • ${selectedItem.unit_price?.toFixed(2) || "0.00"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Package System
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CatalogPackageSystemsTab({ packages, catalogItems, searchQuery, categoryFilter, loading, saving, onCreate, onEdit, onClone, onDelete }: CatalogPackageSystemsTabProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "system" | "mine">("all");

  const filteredPackages = useMemo(() => {
    return packages
      .filter((pkg) => {
        if (filter === "system") return !!pkg.is_system;
        if (filter === "mine") return !pkg.is_system;
        return true;
      })
      .filter((pkg) => {
        const matchesSearch = [pkg.name, pkg.description, pkg.category, pkg.manufacturer, pkg.tier]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = categoryFilter === "all" || pkg.category === categoryFilter;
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        const sortA = a.sort_order ?? 0;
        const sortB = b.sort_order ?? 0;
        if (sortA !== sortB) return sortA - sortB;
        return a.name.localeCompare(b.name);
      });
  }, [packages, filter, searchQuery, categoryFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogPackageGroup[]>();
    for (const pkg of filteredPackages) {
      const key = pkg.manufacturer || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(pkg);
    }
    return Array.from(map.entries());
  }, [filteredPackages]);

  const itemLookup = useMemo(() => new Map(catalogItems.map((item) => [item.id, item])), [catalogItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading package systems…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-1 rounded-md bg-muted/30 p-1">
          {(["all", "system", "mine"] as const).map((entry) => (
            <Button key={entry} size="sm" variant={filter === entry ? "default" : "ghost"} onClick={() => setFilter(entry)} className="h-8 capitalize">
              {entry === "mine" ? "My Packages" : entry}
            </Button>
          ))}
        </div>
        <Button onClick={onCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Package System
        </Button>
      </div>

      {grouped.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {filter === "mine"
                ? "You haven’t created any package systems yet. Clone a prebuilt one or build from scratch."
                : "No package systems found for the current filters."}
            </p>
          </CardContent>
        </Card>
      )}

      {grouped.map(([manufacturer, list]) => (
        <div key={manufacturer} className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{manufacturer}</h3>
            <span className="text-xs text-muted-foreground">{list.length} package systems</span>
          </div>
          <div className="grid gap-3">
            {list.map((pkg) => {
              const isOpen = openId === pkg.id;
              const lines = (pkg.items || []).map((line) => {
                const item = line.catalog_item || (line.catalog_item_id ? itemLookup.get(line.catalog_item_id) : undefined);
                const qty = evaluateFormula(line.default_qty_formula || line.quantity?.toString() || "1");
                const unitPrice = item?.unit_price || 0;
                return {
                  id: line.id,
                  name: item?.name || line.custom_label || "Catalog item",
                  unit: item?.unit_type || "unit",
                  qty,
                  lineTotal: qty * unitPrice,
                  section: line.section || (item?.type === "Labor" ? "labor" : "material"),
                  formula: line.default_qty_formula || line.quantity?.toString() || "1",
                  unitPrice,
                };
              });
              const total = lines.reduce((sum, line) => sum + line.lineTotal, 0);

              return (
                <Card key={pkg.id} className="overflow-hidden">
                  <div className="flex items-center justify-between p-4 transition-colors hover:bg-muted/30">
                    <button type="button" onClick={() => setOpenId(isOpen ? null : pkg.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                      {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                      <Package className="h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">{pkg.name}</div>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          {pkg.sku && <span className="font-mono">{pkg.sku}</span>}
                          {pkg.category && <span>{pkg.category}</span>}
                        </div>
                      </div>
                    </button>
                    <div className="ml-3 flex items-center gap-2">
                      {pkg.tier && <Badge className={TIER_BADGE[pkg.tier] || ""}>{pkg.tier}</Badge>}
                      {pkg.is_system ? <Badge variant="outline" className="text-[10px]">Prebuilt</Badge> : <Badge variant="outline" className="border-primary/40 text-[10px] text-primary">Custom</Badge>}
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onClone(pkg)} title="Clone">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => onEdit(pkg)}
                        title={pkg.is_system ? "Edit (creates an editable copy under your org)" : "Edit"}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {!pkg.is_system && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" onClick={() => onDelete(pkg)} title="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t bg-muted/20 p-4">
                      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>Sample total based on 30 SQ / 120 LF eaves / 80 LF rakes / 40 LF ridge.</span>
                        {pkg.description && <span>{pkg.description}</span>}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="text-left text-muted-foreground">
                            <tr>
                              <th className="py-1 pr-2">Item</th>
                              <th className="py-1 pr-2">Section</th>
                              <th className="py-1 pr-2">Formula</th>
                              <th className="py-1 pr-2 w-[72px]">Unit</th>
                              <th className="py-1 pr-2 w-[72px]">Qty</th>
                              <th className="py-1 pr-2 w-[88px]">Price</th>
                              <th className="w-[100px] py-1 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((line) => (
                              <tr key={line.id} className="border-t border-border/60">
                                <td className="py-2 pr-2 font-medium text-foreground">{line.name}</td>
                                <td className="py-2 pr-2 capitalize">{line.section}</td>
                                <td className="py-2 pr-2 font-mono text-[11px]">{line.formula}</td>
                                <td className="py-2 pr-2">{line.unit}</td>
                                <td className="py-2 pr-2 tabular-nums">{qtyFormatter.format(line.qty)}</td>
                                <td className="py-2 pr-2 tabular-nums">${line.unitPrice.toFixed(2)}</td>
                                <td className="py-2 text-right tabular-nums">${line.lineTotal.toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="border-t font-semibold">
                              <td colSpan={6} className="py-2 text-right">Sample Total</td>
                              <td className="py-2 text-right tabular-nums">${total.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {saving && (
        <div className="flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving package system…
        </div>
      )}
    </div>
  );
}

export function createInitialPackageEditorState(): PackageEditorState {
  return { open: false, source: null, cloneMode: false };
}
